import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { cartStore } from 'features/cart/stores/cart.slice';
import { accountStore } from 'features/account/stores/account.slice';
import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import { IBillingProfile } from '../../types/checkout.types';
import {
   setDefaultPaymentDialogOpen,
   setDeletePaymentDialogOpen,
   setPaymentToDefault,
   setPaymentToDelete,
} from '../../stores/checkout-payment.slice';
import EditPaymentDialog from 'features/profile/components/EditPaymentDialog';
import { changeCustomModalOpen } from 'components/utils/dialog.helper';

interface IMorePaymentOptionProps {
   payment?: IBillingProfile;
}

const MorePaymentOption = ({ payment }: IMorePaymentOptionProps) => {
   const dispatch = useAppDispatch();
   const { cart } = useAppSelector(cartStore);
   const { billingProfiles } = useAppSelector(accountStore);
   const [anchorEl, setAnchorEl] = useState<{ [key: string]: HTMLElement | null }>({});
   const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
   const [editPaymentId, setEditPaymentId] = useState<string>('');

   const foundBillingProfile = payment
      ? payment
      : billingProfiles?.find((findProfile) => findProfile.id === cart?.billingProfileId);

   const handleOptionClick = (event: React.MouseEvent<HTMLButtonElement>, paymentId: string) => {
      setAnchorEl((prev) => ({ ...prev, [paymentId]: event.currentTarget }));
   };

   const handleOptionClose = (paymentId: string) => {
      setAnchorEl((prev) => ({ ...prev, [paymentId]: null }));
   };

   const handlePaymentEdit = (id: string) => {
      handleOptionClose(id);
      setEditPaymentId(id);
      setEditPaymentDialogOpen(true);
   };

   const handlePaymentDelete = (payment: IBillingProfile) => {
      handleOptionClose(payment.id);
      dispatch(setDeletePaymentDialogOpen(true));
      dispatch(setPaymentToDelete(payment));
   };

   const handleSetDefault = (payment: IBillingProfile) => {
      handleOptionClose(payment.id);
      dispatch(setDefaultPaymentDialogOpen(true));
      dispatch(setPaymentToDefault(payment));
   };

   useEffect(() => {
      changeCustomModalOpen(editPaymentDialogOpen);
   }, [editPaymentDialogOpen]);

   return (
      <>
         {foundBillingProfile ? (
            <>
               <Tooltip title="More Options">
                  <IconButton onClick={(event) => handleOptionClick(event, foundBillingProfile.id)}>
                     <MoreVertIcon />
                  </IconButton>
               </Tooltip>
               <Menu
                  anchorEl={anchorEl[foundBillingProfile.id]}
                  anchorOrigin={{
                     vertical: 'bottom',
                     horizontal: 'right',
                  }}
                  transformOrigin={{
                     vertical: 'top',
                     horizontal: 'right',
                  }}
                  open={Boolean(anchorEl[foundBillingProfile.id])}
                  onClose={() => handleOptionClose(foundBillingProfile.id)}
               >
                  <MenuItem onClick={() => handlePaymentEdit(foundBillingProfile.id)}>
                     <ListItemIcon>
                        <EditIcon />
                     </ListItemIcon>
                     <ListItemText>Edit</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={() => handlePaymentDelete(foundBillingProfile)}>
                     <ListItemIcon>
                        <DeleteIcon />
                     </ListItemIcon>
                     <ListItemText>Delete</ListItemText>
                  </MenuItem>
                  {!foundBillingProfile.default ? (
                     <MenuItem onClick={() => handleSetDefault(foundBillingProfile)}>
                        <ListItemIcon>
                           <StarIcon />
                        </ListItemIcon>
                        <ListItemText>Set Default</ListItemText>
                     </MenuItem>
                  ) : null}
               </Menu>
               {editPaymentDialogOpen && (
                  <EditPaymentDialog
                     open={editPaymentDialogOpen}
                     editPaymentId={editPaymentId}
                     setEditPaymentDialogOpen={setEditPaymentDialogOpen}
                  />
               )}
            </>
         ) : null}
      </>
   );
};

export default MorePaymentOption;
